import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileCheck2, FileUp, Gavel, Loader2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Modal } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { MOTIVOS_DE_BAJA, type MotivoDeBaja, type Resolucion } from './contrato';
import { Campo, ENTRADA, ListaDeTitulos, SeleccionDePersona, type Deudor } from './piezas';

type Tipo = 'PRESCRIPCION' | 'BAJA';

const TAMANIO_MAXIMO = 5 * 1024 * 1024;

export function ModalDeExtincion({
  textoInicial = '',
  alCerrar,
  alRegistrar,
  alVerConstancia,
}: {
  textoInicial?: string;
  alCerrar: () => void;
  alRegistrar: () => void;
  alVerConstancia: (r: Resolucion) => void;
}) {
  const [busqueda, setBusqueda] = useState(textoInicial);
  const [deudor, setDeudor] = useState<Deudor | null>(null);
  const [desmarcados, setDesmarcados] = useState<Set<number>>(new Set());

  const [tipo, setTipo] = useState<Tipo>('PRESCRIPCION');
  const [motivo, setMotivo] = useState<MotivoDeBaja | ''>('');
  const [numero, setNumero] = useState('');
  const [fecha, setFecha] = useState('');
  const [tramite, setTramite] = useState('');
  const [observacion, setObservacion] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hecha, setHecha] = useState<Resolucion | null>(null);
  const selector = useRef<HTMLInputElement>(null);

  const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });
  const seleccion = deudor?.titulos.filter((t) => !desmarcados.has(t.id)) ?? [];
  const total = seleccion.reduce((suma, t) => suma + t.capital, 0);

  const falta =
    seleccion.length === 0
      ? 'Marque al menos un título'
      : tipo === 'BAJA' && !motivo
        ? 'Diga por qué se da de baja'
        : !numero.trim()
          ? 'Falta el número de la resolución'
          : !fecha
            ? 'Falta la fecha de la resolución'
            : !archivo
              ? 'Adjunte el PDF de la resolución'
              : null;

  const registrar = useMutation({
    mutationFn: () => {
      const formulario = new FormData();
      formulario.append('tipo', tipo);
      if (tipo === 'BAJA' && motivo) formulario.append('motivo', motivo);
      seleccion.forEach((t) => formulario.append('titulos', String(t.id)));
      formulario.append('numero', numero.trim());
      formulario.append('fecha', fecha);
      if (tramite.trim()) formulario.append('tramite', tramite.trim());
      if (observacion.trim()) formulario.append('observacion', observacion.trim());
      formulario.append('archivo', archivo as File);
      return api.subir<Resolucion>('/titulos/resoluciones', formulario);
    },
    onSuccess: (r) => {
      notificar.exito(
        `${r.titulos} ${r.titulos === 1 ? 'título extinguido' : 'títulos extinguidos'} · $${r.capital.toFixed(2)}`,
        r.tipo === 'PRESCRIPCION' ? 'Prescripción registrada' : 'Baja registrada',
      );
      setHecha(r);
      alRegistrar();
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo registrar';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  function elegir(d: Deudor) {
    setDeudor(d);
    setDesmarcados(new Set());
    setError(null);
  }

  function elegirArchivo(f: File | undefined) {
    setError(null);
    if (!f) return;
    if (f.type && f.type !== 'application/pdf') {
      setError('EL ARCHIVO NO ES UN PDF');
      return;
    }
    if (f.size > TAMANIO_MAXIMO) {
      setError('EL PDF PASA DE 5 MB');
      return;
    }
    setArchivo(f);
  }

  return (
    <Modal
      abierto
      alCerrar={alCerrar}
      titulo="Prescripción o baja · Art. 8"
      subtitulo="Se registra lo que ya resolvió jurídico, con el PDF de su resolución"
      icono={Gavel}
      ancho="ventanilla"
      cerrarAlPulsarFuera={false}
    >
      {hecha ? (
        <div className="rounded-2xl border border-marca-200 bg-marca-50 p-10 text-center">
          <CheckCircle2 className="mx-auto size-10 text-marca-600" aria-hidden />
          <p className="mt-3 text-sm font-bold uppercase text-marca-900">
            {hecha.tipo === 'PRESCRIPCION' ? 'Prescripción registrada' : 'Baja registrada'}
          </p>
          <p className="mt-1 text-xs uppercase text-marca-800">
            {hecha.titulos} {hecha.titulos === 1 ? 'título' : 'títulos'} · $
            {hecha.capital.toFixed(2)} · resolución {hecha.numero}
          </p>
          <div className="mt-5 flex justify-center gap-2">
            <button
              type="button"
              onClick={() => alVerConstancia(hecha)}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-marca-600 px-4 text-xs font-bold uppercase text-white hover:bg-marca-700"
            >
              <FileCheck2 className="size-4" aria-hidden />
              Constancia para el contribuyente
            </button>
            <button
              type="button"
              onClick={alCerrar}
              className="inline-flex h-10 items-center rounded-lg border border-armazon-200 bg-white px-4 text-xs font-semibold uppercase text-armazon-700 hover:bg-armazon-50"
            >
              Cerrar
            </button>
          </div>
        </div>
      ) : (
        <>
          <SeleccionDePersona
            pregunta="De quién son los títulos"
            busqueda={busqueda}
            alBuscar={setBusqueda}
            deudor={deudor}
            alElegir={elegir}
            alLimpiar={() => {
              setDeudor(null);
              setBusqueda('');
            }}
            textoInicial={textoInicial}
          />

          {deudor === null ? (
            <p className="rounded-2xl border border-armazon-200 bg-white p-12 text-center text-sm uppercase text-armazon-400">
              Busque a la persona: una resolución es de una sola
            </p>
          ) : (
            <div className="grid gap-5 lg:grid-cols-[1fr_22rem]">
              <div className="space-y-4">
                <ListaDeTitulos
                  titulos={deudor.titulos}
                  desmarcados={desmarcados}
                  alCambiar={setDesmarcados}
                />
                <p className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-amber-900">
                  <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
                  <span>
                    <span className="font-semibold">La prescripción la pide el contribuyente.</span>{' '}
                    No se declara de oficio (Procuraduría General del Estado, 2026). Se exigen cinco
                    años desde que cada título fue exigible.
                  </span>
                </p>
              </div>

              <div className="space-y-4 rounded-2xl border border-armazon-200 bg-white p-5">
                <div className="grid grid-cols-2 gap-1 rounded-lg bg-armazon-100 p-1">
                  {(['PRESCRIPCION', 'BAJA'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTipo(t)}
                      className={`rounded-md py-2 text-[11px] font-bold uppercase transition-colors ${
                        tipo === t
                          ? 'bg-white text-armazon-900 shadow-sm'
                          : 'text-armazon-500 hover:text-armazon-700'
                      }`}
                    >
                      {t === 'PRESCRIPCION' ? 'Prescripción' : 'Baja'}
                    </button>
                  ))}
                </div>

                {tipo === 'BAJA' && (
                  <Campo etiqueta="Motivo de la baja">
                    <select
                      value={motivo}
                      onChange={(e) => setMotivo(e.target.value as MotivoDeBaja | '')}
                      className={ENTRADA}
                    >
                      <option value="">ELIJA…</option>
                      {MOTIVOS_DE_BAJA.map((m) => (
                        <option key={m.valor} value={m.valor}>
                          {m.etiqueta.toLocaleUpperCase('es')}
                        </option>
                      ))}
                    </select>
                  </Campo>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Campo etiqueta="N.º de resolución">
                    <input
                      value={numero}
                      onChange={(e) => setNumero(e.target.value)}
                      maxLength={60}
                      className={`${ENTRADA} uppercase`}
                    />
                  </Campo>
                  <Campo etiqueta="Fecha">
                    <input
                      type="date"
                      value={fecha}
                      max={hoy}
                      onChange={(e) => setFecha(e.target.value)}
                      className={ENTRADA}
                    />
                  </Campo>
                </div>

                <Campo etiqueta="N.º de trámite (opcional)">
                  <input
                    value={tramite}
                    onChange={(e) => setTramite(e.target.value)}
                    maxLength={60}
                    className={`${ENTRADA} uppercase`}
                  />
                </Campo>

                <Campo etiqueta="Observación (opcional)">
                  <textarea
                    value={observacion}
                    onChange={(e) => setObservacion(e.target.value)}
                    maxLength={500}
                    rows={2}
                    className={`${ENTRADA} h-auto py-2`}
                  />
                </Campo>

                <div>
                  <span className="text-[10px] font-semibold uppercase text-armazon-600">
                    PDF de la resolución
                  </span>
                  <input
                    ref={selector}
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={(e) => elegirArchivo(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => selector.current?.click()}
                    className={`mt-1.5 flex w-full items-center gap-2 rounded-lg border border-dashed px-3 py-3 text-left text-xs transition-colors ${
                      archivo
                        ? 'border-marca-300 bg-marca-50 text-marca-800'
                        : 'border-armazon-300 text-armazon-500 hover:bg-armazon-50'
                    }`}
                  >
                    <FileUp className="size-4 shrink-0" aria-hidden />
                    <span className="min-w-0 truncate">
                      {archivo
                        ? `${archivo.name} · ${(archivo.size / 1024).toFixed(0)} KB`
                        : 'Elegir el PDF (hasta 5 MB)'}
                    </span>
                  </button>
                </div>

                <div className="rounded-xl bg-armazon-900 p-4 text-white">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-white/60">
                    Se extinguen
                  </p>
                  <p className="importe mt-1 text-2xl font-bold">${total.toFixed(2)}</p>
                  <p className="text-[11px] uppercase text-white/60">
                    {seleccion.length} {seleccion.length === 1 ? 'título' : 'títulos'} · sin factura
                    ni caja
                  </p>
                </div>

                {(error ?? falta) && (
                  <p
                    role={error ? 'alert' : undefined}
                    className={`text-[11px] font-semibold uppercase ${error ? 'text-red-700' : 'text-armazon-500'}`}
                  >
                    {error ?? falta}
                  </p>
                )}

                <button
                  type="button"
                  disabled={falta !== null || registrar.isPending}
                  onClick={() => {
                    setError(null);
                    registrar.mutate();
                  }}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-armazon-800 text-xs font-bold uppercase text-white transition-colors hover:bg-armazon-900 disabled:opacity-40"
                >
                  {registrar.isPending ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Gavel className="size-4" aria-hidden />
                  )}
                  {tipo === 'PRESCRIPCION' ? 'Registrar la prescripción' : 'Registrar la baja'}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
